import React, { useState, useEffect } from "react";
import { Menu, X, Download } from "lucide-react";
import { NavLink, Link, useNavigate } from "react-router-dom";
import { Logo } from "./Logo";
import { NAV_LINKS } from "../data/content";

const GUIDE_PDF = "/guida-tutorial-pushapp.pdf";

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-40 transition-all duration-300 ${
        scrolled ? "bg-white/85 backdrop-blur-xl border-b border-gray-100 shadow-sm" : "bg-white/40 backdrop-blur-md"
      }`}
      data-testid="main-navbar"
    >
      <nav className="mx-auto max-w-7xl px-5 sm:px-8 h-[88px] flex items-center justify-between">
        <Logo onClick={() => navigate("/")} />

        <div className="hidden lg:flex items-center gap-9">
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={`nav-link-${l.to === "/" ? "home" : l.to.replace("/", "")}`}
              className={({ isActive }) =>
                `font-body text-[17px] font-semibold transition-colors ${
                  isActive ? "text-brand" : "text-ink hover:text-brand"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </div>

        <div className="hidden lg:flex items-center gap-3.5">
          <a
            href={GUIDE_PDF}
            download
            target="_blank"
            rel="noreferrer"
            data-testid="nav-download-guide"
            className="inline-flex items-center gap-2 rounded-full border-2 border-brand px-5 py-2.5 font-body font-semibold text-brand text-[15px] transition-all duration-300 hover:bg-brand hover:text-white"
          >
            <Download size={18} strokeWidth={2.5} /> Scarica guida tutorial
          </a>
          <Link
            to="/assistenza"
            data-testid="nav-contact-button"
            className="rounded-full bg-brand px-7 py-3 font-body font-semibold text-white text-[16px] shadow-lg shadow-brand/25 transition-all duration-300 hover:bg-brand-hover hover:-translate-y-0.5"
          >
            Assistenza
          </Link>
        </div>

        <button
          className="lg:hidden text-brand-deep"
          onClick={() => setOpen(!open)}
          data-testid="mobile-menu-toggle"
          aria-label="Menu"
        >
          {open ? <X size={28} /> : <Menu size={28} />}
        </button>
      </nav>

      {open && (
        <div className="lg:hidden bg-white border-t border-gray-100 px-5 py-4 flex flex-col gap-1" data-testid="mobile-menu">
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              onClick={() => setOpen(false)}
              className="text-left py-3 font-body font-medium text-ink hover:text-brand"
              data-testid={`mobile-nav-${l.to === "/" ? "home" : l.to.replace("/", "")}`}
            >
              {l.label}
            </NavLink>
          ))}
          <Link
            to="/checklist"
            onClick={() => setOpen(false)}
            className="text-left py-3 font-body font-medium text-ink hover:text-brand"
          >
            Errori & Checklist
          </Link>
          <a
            href={GUIDE_PDF}
            download
            target="_blank"
            rel="noreferrer"
            data-testid="mobile-download-guide"
            className="mt-2 inline-flex items-center justify-center gap-2 rounded-full border-2 border-brand px-6 py-3 font-body font-semibold text-brand"
          >
            <Download size={16} strokeWidth={2.5} /> Scarica guida tutorial
          </a>
          <Link
            to="/assistenza"
            onClick={() => setOpen(false)}
            className="mt-2 rounded-full bg-brand px-6 py-3 font-body font-semibold text-white text-center"
            data-testid="mobile-contact-button"
          >
            Assistenza
          </Link>
        </div>
      )}
    </header>
  );
};
